import React from 'react';
import { FieldConfig } from '../../../playground/common/field-types';
import { renderRateScheduleField } from '../../../playground/endpoints/_shared';
import { ContributionListField } from '../_shared';
import {
  CAPACITY_OBJECTIVE_OPTIONS,
  COMPENSATION_PLAN_OPTIONS,
  FamilySavingsV2FormValues,
  GENDER_OPTIONS,
  PAYMENT_PERIODICITY_OPTIONS,
  RATE_MODE_OPTIONS,
  SAVINGS_MODE_OPTIONS,
} from './schema';

const CLIENTE = 'Datos del cliente';
const POLIZA = 'Detalle de la póliza';
const CAPITALES = 'Capitales';
const MODO = 'Modo y campos específicos';
const APORTES = 'Aportes y primas';

const isGoalMode = (v: FamilySavingsV2FormValues) => v.savings_mode === 'goal_value';
const isCapacityMode = (v: FamilySavingsV2FormValues) => v.savings_mode === 'savings_capacity';
const isWithdrawalMode = (v: FamilySavingsV2FormValues) => v.savings_mode === 'withdrawal_goal';
const isFixedRate = (v: FamilySavingsV2FormValues) => v.rate_mode === 'fixed';
const isSteppedRate = (v: FamilySavingsV2FormValues) => v.rate_mode === 'stepped';
// Narrow on the discriminant so `capacity_objective` (savings_capacity-only)
// is accessible.
const isCapacityGoal = (v: FamilySavingsV2FormValues) => (
  v.savings_mode === 'savings_capacity' && v.capacity_objective === 'goal'
);
const showsGoalFields = (v: FamilySavingsV2FormValues) => isGoalMode(v) || isCapacityGoal(v);

export const FAMILY_SAVINGS_V2_FIELDS: readonly FieldConfig<FamilySavingsV2FormValues>[] = [
  {
    key: 'date_of_birth', label: 'Fecha de nacimiento', kind: 'date', section: CLIENTE,
  },
  {
    key: 'gender', label: 'Género', kind: 'select', options: GENDER_OPTIONS, section: CLIENTE,
  },
  {
    key: 'compensation_plan',
    label: 'Plan de compensación',
    kind: 'select',
    options: COMPENSATION_PLAN_OPTIONS,
    section: POLIZA,
  },
  {
    key: 'death_insured_capital',
    label: 'Capital muerte (UF)',
    kind: 'number',
    colClassName: 'col-md-6',
    section: CAPITALES,
  },
  {
    key: 'accidental_death_insured_capital',
    label: 'Capital muerte accidental (UF)',
    kind: 'number',
    colClassName: 'col-md-6',
    section: CAPITALES,
  },
  {
    key: 'itp_insured_capital',
    label: 'Capital ITP 2/3 (UF)',
    kind: 'number',
    colClassName: 'col-md-6',
    section: CAPITALES,
  },
  {
    key: 'serious_illness_insured_capital',
    label: 'Capital enfermedades graves (UF)',
    kind: 'number',
    colClassName: 'col-md-6',
    section: CAPITALES,
  },
  {
    key: 'savings_mode',
    label: 'Modo de proyección',
    kind: 'select',
    options: SAVINGS_MODE_OPTIONS,
    section: MODO,
  },
  {
    key: 'capacity_objective',
    label: 'Objetivo (capacidad de ahorro)',
    kind: 'select',
    options: CAPACITY_OBJECTIVE_OPTIONS,
    visibleWhen: isCapacityMode,
    section: MODO,
  },
  {
    key: 'goal',
    label: 'Objetivo (UF)',
    kind: 'number',
    colClassName: 'col-md-6',
    visibleWhen: showsGoalFields,
    section: MODO,
  },
  {
    key: 'goal_horizon_month',
    label: 'Mes del retiro',
    kind: 'number',
    colClassName: 'col-md-6',
    visibleWhen: showsGoalFields,
    section: MODO,
  },
  {
    key: 'withdrawal_amount',
    label: 'Monto de retiro (UF)',
    kind: 'number',
    colClassName: 'col-md-6',
    visibleWhen: isWithdrawalMode,
    section: MODO,
  },
  {
    key: 'withdrawal_period_months',
    label: 'Meses entre retiros',
    kind: 'number',
    colClassName: 'col-md-6',
    visibleWhen: isWithdrawalMode,
    section: MODO,
  },
  {
    key: 'rate_mode',
    label: 'Modo de tasa',
    kind: 'select',
    options: RATE_MODE_OPTIONS,
    section: MODO,
  },
  {
    key: 'projection_rate',
    label: 'Tasa de proyección anual',
    kind: 'number',
    step: 0.0001,
    visibleWhen: isFixedRate,
    section: MODO,
  },
  {
    key: 'projection_rate_schedule',
    label: 'Tramos de tasa',
    kind: 'custom',
    visibleWhen: isSteppedRate,
    render: renderRateScheduleField,
    section: MODO,
  },
  {
    key: 'agreed_premium',
    label: 'Prima acordada (UF por período)',
    kind: 'number',
    visibleWhen: isCapacityMode,
    section: APORTES,
  },
  {
    key: 'payment_periodicity',
    label: 'Periodicidad de pago',
    kind: 'select',
    options: PAYMENT_PERIODICITY_OPTIONS,
    section: APORTES,
  },
  // Replaces v1's three flat fields (initial_contribution,
  // unscheduled_annual_contribution, direct_contribution): a v1 opening
  // contribution is a `once` entry at start_month 0.
  {
    key: 'contributions',
    label: 'Aportes',
    kind: 'custom',
    render: (form) => (
      <ContributionListField
        form={form}
        name="contributions"
        addLabel="+ Agregar aporte"
        emptyLabel="Sin aportes."
      />
    ),
    section: APORTES,
  },
];
